import Image from 'next/image';
import Link from 'next/link';
import { isFree } from '../../../../utils/price';
import { c } from '../../../../utils/tailwind-merge';
import { Badge } from '../../../atoms/Badge';
import { Price } from '../../../atoms/Price';
import { ProductCardHorizontalProps } from './ProductCardHorizontal.types';
import { ProductCardHorizontalBookmarkButton } from './ProductCardHorizontalBookmarkButton';

export const ProductCardHorizontal = ({
  name,
  productUrl,
  imageUrl,
  publisherName,
  publisherUrl,
  price,
  isEntitled,
  microcopy,
  openInNewTab,
  showFavorite,
  isFavorite,
  onFavorite,
  className = '',
}: ProductCardHorizontalProps) => {
  return (
    <article
      data-test="product-card-horizontal"
      className={c(
        'group relative flex w-full min-w-[278px] flex-row overflow-hidden rounded-lg border border-line bg-surface',
        'transition-[box-shadow,border-color] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]',
        'hover:border-transparent hover:shadow-md',
        className
      )}
    >
      <div className="relative aspect-[3/2] h-[88px] shrink-0">
        <Image
          fill
          alt=""
          aria-hidden={true}
          src={imageUrl}
          className="object-cover"
        />
        {showFavorite && isFavorite != null && (
          <ProductCardHorizontalBookmarkButton
            isFavorite={isFavorite}
            addToFavoritesLabel={microcopy.addToFavorites ?? ''}
            removeFromFavoritesLabel={microcopy.removeFromFavorites ?? ''}
            onFavorite={onFavorite}
          />
        )}
      </div>
      <div className="flex flex-col gap-1 overflow-hidden px-3 py-2 text-sm leading-5 tracking-[-0.028px]">
        <Link
          href={productUrl}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          data-test="product-card-horizontal-name"
          className="focus-outline rounded after:absolute after:inset-0"
        >
          <div className="w-full truncate text-content">{name}</div>
        </Link>
        <Link
          href={publisherUrl}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          data-test="product-card-horizontal-publisher"
          className="focus-outline relative z-10 w-full truncate rounded text-content-subtle hover:text-link-hover"
        >
          {publisherName}
        </Link>
        {isEntitled ? (
          <div
            data-test="product-card-horizontal-owned"
            className="truncate text-grey-400 dark:text-content-subtle"
          >
            {microcopy.owned}
          </div>
        ) : (
          <div className="flex h-5 flex-wrap items-center gap-2 overflow-hidden">
            <Price
              className="text-content"
              value={isFree(price) ? undefined : String(price.currentPrice)}
              currency={price.currency}
              microcopy={{ free: microcopy.free }}
              data-test="product-card-horizontal-current-price"
            />
            {price.currentPrice !== price.originalPrice && (
              <Price
                className="text-content-subtle line-through"
                value={String(price.originalPrice)}
                currency={price.currency}
                microcopy={{ free: microcopy.free }}
                data-test="product-card-horizontal-original-price"
              />
            )}
            {!!price.discountPercent && (
              <Badge
                variant="sale"
                text={`-${Math.round(price.discountPercent)}%`}
                className="rounded-[4px] py-0"
                data-test="product-card-horizontal-discount"
              />
            )}
          </div>
        )}
      </div>
    </article>
  );
};
